import React, { createContext, ReactNode, useContext, useState } from 'react';
import { MOBILE_WIDTH_BREAKPOINT, TABLET_WIDTH_BREAKPOINT } from 'shared/enums/DeviceSizes';
import useResponsiveObserver, { observerCallback } from 'shared/hooks/useResponsiveObserver';

interface IResolutionProvider {
  isMobile: boolean;
  isTablet: boolean;
  isTouchDevice: boolean;
  currentHeight: number;
}

const ResolutionContext = createContext<IResolutionProvider>({
  isMobile: false,
  isTablet: false,
  isTouchDevice: false,
  currentHeight: 0,
});

interface ResolutionProviderProps {
  children: ReactNode;
}

export const useResolutionProvider = (): IResolutionProvider => useContext<IResolutionProvider>(ResolutionContext);

const ResolutionProvider = ({ children }: ResolutionProviderProps): JSX.Element => {
  const rootElement = document.getElementById('root');
  const [isMobile, setIsMobile] = useState<boolean>(
    rootElement ? rootElement.getBoundingClientRect().width <= MOBILE_WIDTH_BREAKPOINT : false,
  );

  const [isTablet, setIsTablet] = useState<boolean>(
    rootElement ? rootElement.getBoundingClientRect().width <= TABLET_WIDTH_BREAKPOINT : false,
  );

  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(
    !!('ontouchstart' in window || navigator.maxTouchPoints > 0),
  );

  const [currentHeight, setCurrentHeight] = useState<number>(window.innerHeight);

  useResponsiveObserver({
    element: rootElement,
    callback: (entries: readonly ResizeObserverEntry[]) =>
      observerCallback(entries, setIsMobile, MOBILE_WIDTH_BREAKPOINT),
  });

  useResponsiveObserver({
    element: rootElement,
    callback: (entries: readonly ResizeObserverEntry[]) =>
      observerCallback(entries, setIsTablet, TABLET_WIDTH_BREAKPOINT),
  });

  useResponsiveObserver({
    element: rootElement,
    callback: () => setIsTouchDevice(!!('ontouchstart' in window || navigator.maxTouchPoints > 0)),
  });

  useResponsiveObserver({
    element: rootElement,
    callback: (entries: readonly ResizeObserverEntry[]) =>
      observerCallback(entries, setCurrentHeight, window.innerHeight, true),
  });

  return (
    <ResolutionContext.Provider
      value={{
        isMobile,
        isTablet,
        isTouchDevice,
        currentHeight,
      }}
    >
      {children}
    </ResolutionContext.Provider>
  );
};

export default ResolutionProvider;
